import CreateWorkspace from '@/components/main/CreateWorkspace'
import TooltipWrapper from '@/components/main/Tooltip'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ROUTES } from '@/constants/routes'
import { useAuthContext } from '@/context'
import { useFinderContext } from '@/context/finder-context'
import { useLoading } from '@/context/loading-context'
import { Permissions } from '@/hooks/permissions/const'
import { PermissionsWrapper } from '@/hooks/permissions/usePermissions'
import useModuleAccess from '@/hooks/useModuleAccess'
import { queryClient } from '@/queryClient'
import { useSwitchAccount } from '@/services/auth/mutations'
import { keyUser } from '@/services/auth/queries'
import { ArrowDownUp, Check, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import ReorderWorkspace from './ReorderWorkspace'

type Props = {
    isOpen?: boolean
    setIsOpen?: (value: boolean) => void
    trigger: React.ReactNode
    navigateTo?: string
}

const SwitchWorkspace = ({ setIsOpen, trigger, navigateTo }: Props) => {
    const navigate = useNavigate()
    const { setSelectedItems } = useFinderContext()
    const { orgAccount, accounts, setOrgAccount, isLoading, activeBusinessAccount, user } = useAuthContext()
    const { showLoading, hideLoading } = useLoading()
    const orgAccounts = useMemo(() => {
        const sorted = [...(accounts || [])].sort((a, b) => (a.org?.name || '').localeCompare(b.org?.name || ''))
        return sorted
    }, [accounts])
    const [openModal, setOpenModal] = useState(false)
    const { canAccess, setUpgrade } = useModuleAccess({ module: 'workspace' })
    const isMobileConsumer = user?.experience_variant === 'mobile_consumer'
    const canCreateAdditionalWorkspaces = user?.capabilities?.can_create_additional_workspaces !== false

    const { mutate: switchAccount } = useSwitchAccount({
        refetch: [keyUser],
        onSuccess: () => {
            setIsOpen?.(false)
            navigate(navigateTo || ROUTES.DASHBOARD_ROOT)
            queryClient.invalidateQueries()
            hideLoading()
        },
    })

    const handleChangeWorkspace = (value: string) => {
        setIsOpen?.(false)
        setSelectedItems([])
        const account = orgAccounts.find(account => account.id === value)
        setOrgAccount(account)
        switchAccount({
            account: value,
        })
        showLoading('Switching workspace...')
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
            <DropdownMenuContent className='w-56 bg-white border border-gray-200 shadow-2xl' align='start' forceMount>
                <DropdownMenuLabel className='flex items-center justify-between font-normal'>
                    <p className='text-sm font-medium leading-none'>Switch Workspace</p>

                    {orgAccounts.length > 1 ? (
                        <ReorderWorkspace
                            trigger={
                                <TooltipWrapper content='Reorder'>
                                    <ArrowDownUp size={17} />
                                </TooltipWrapper>
                            }
                            initialWorkspaces={orgAccounts}
                            onClose={() => setIsOpen?.(false)}
                            isLoading={isLoading}
                        />
                    ) : null}
                </DropdownMenuLabel>
                <DropdownMenuSeparator className='bg-gray-300' />

                <div className='max-h-[70vh] overflow-y-auto no-scrollbar'>
                    {orgAccounts.map((account, index) => {
                        const isActive = account.id === orgAccount?.id
                        return (
                            <DropdownMenuGroup key={index}>
                                <DropdownMenuItem
                                    className={`py-2 ${isActive ? 'bg-muted cursor-default' : ''}`}
                                    onClick={() => !isActive && handleChangeWorkspace(account.id)}
                                    disabled={isActive}
                                >
                                    <span>{account.org?.name}</span>
                                    {isActive && <Check className='w-4 h-4 ml-auto' />}
                                </DropdownMenuItem>
                            </DropdownMenuGroup>
                        )
                    })}
                </div>

                <DropdownMenuSeparator className='bg-gray-300' />

                {isMobileConsumer || !canCreateAdditionalWorkspaces ? (
                    <div className='px-3 py-2 text-xs text-gray-600'>
                        Mobile accounts include one workspace. Agency accounts can create additional workspaces.
                    </div>
                ) : (
                    <>
                        <CreateWorkspace
                            open={openModal}
                            onOpenChange={value => {
                                if (!value || canAccess) {
                                    setOpenModal(value)
                                }

                                if (value && !canAccess) {
                                    setUpgrade('true')
                                }
                            }}
                        />
                        <PermissionsWrapper
                            businessAccount={activeBusinessAccount}
                            requiredPermissions={[Permissions.CREATE_ANY_WORKSPACE]}
                        >
                            <DropdownMenuItem
                                className='cursor-pointer p-0 focus:bg-transparent data-[highlighted]:bg-transparent'
                                onSelect={e => {
                                    e.preventDefault()
                                    setIsOpen?.(false)
                                    if (!canAccess) {
                                        setUpgrade('true')
                                        return
                                    }
                                    setOpenModal(true)
                                }}
                            >
                                <div className='flex items-center justify-center w-full gap-3 py-2 border shadow-sm bg-primary-light hover:!bg-primary-light text-sm rounded-sm'>
                                    <Plus size={18} />
                                    <span>New workspace</span>
                                </div>
                            </DropdownMenuItem>
                        </PermissionsWrapper>
                    </>
                )}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export default SwitchWorkspace
